import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Injector,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AppInjector } from '@sc/data-access-app-injector';
import { ConfigurationService } from '@sc/data-access-configuration';
import { InsightService } from '@sc/data-access-http-services';
import { SandboxService } from '@sc/data-access-state-management';
import { StorageService } from '@sc/data-access-storage';
import { AuthService } from '@sc/feature-auth-okta';
import { DialogService } from '@sc/feature-dialog';
import { FocusManagerService } from '@sc/feature-focus-manager';
import { LoadingService, MainLoaderComponent } from '@sc/feature-loader';
import { Subject, combineLatest } from 'rxjs';

@Component({
  selector: 'sc-root-fitter',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MainLoaderComponent, RouterLink, RouterOutlet, TranslateModule],
  template: `
    <sc-main-loader></sc-main-loader>
    <a
      [routerLink]="[getRouterLink()]"
      queryParamsHandling="preserve"
      fragment="sc-container-id"
      (click)="onLinkClick('sc-container-id')"
      >{{
        'GlobalApplicativeConstants.accessibility.avoidanceLink' | translate
      }}</a
    >
    <router-outlet></router-outlet>
  `,
})
export class AppComponent implements OnDestroy {
  private inactivityDelay;
  private userActivity;
  private readonly userInactive: Subject<any> = new Subject();

  constructor(
    public injector: Injector,
    public configurationService: ConfigurationService,
    public sandboxService: SandboxService,
    private readonly authService: AuthService,
    public loadingService: LoadingService,
    public dialog: DialogService,
    private readonly insightService: InsightService,
    private readonly storage: StorageService,
    private readonly route: Router,
    private readonly focusManager: FocusManagerService
  ) {
    AppInjector.setInjector(injector);

    combineLatest([
      this.authService.getAuthState$(),
      this.authService.getUserGroups$(),
    ]).subscribe(([isauth, userGroups]) => {
      if (
        isauth.isAuthenticated &&
        this.configurationService.getConfig().autoLogoutDelay?.length > 0
      ) {
        this.inactivityDelay =
          60000 * this.configurationService.getLogoutDelay(userGroups); // delay config is minutes
        this.launchInactivityTimeout();
        this.userInactive.subscribe(async () => {
          this.insightService.trackEvent({
            name: 'FrontApp logout forced caused by inactivity',
          });
          await this.authService.logout();
          this.storage.setItem('forcelogout', true);
          location.reload();
        });
      }
      this.sandboxService.setIsAuth(isauth.isAuthenticated);
    });

    this.authService.getAuthState$().subscribe((authState) => {
      if (authState.isAuthenticated) this.sandboxService.checkSubscription();
      this.sandboxService.setIsAuth(authState.isAuthenticated);
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.userActivity);
  }

  launchInactivityTimeout() {
    if (this.userActivity) clearTimeout(this.userActivity);
    this.userActivity = setTimeout(
      () => this.userInactive.next(undefined),
      this.inactivityDelay
    );
  }

  getRouterLink() {
    const routeWithoutFragment = this.route.parseUrl(this.route.url);
    routeWithoutFragment.fragment = null;
    return routeWithoutFragment.toString();
  }

  onLinkClick(linkId: string) {
    this.focusManager.moveFocusStartingPoint(`#${linkId}`);
  }

  @HostListener('window:mousemove') refreshUserState() {
    clearTimeout(this.userActivity);
    this.launchInactivityTimeout();
  }
}
